import { useCallback, useEffect, useRef, useState } from "react";
import generateMusic from "../utils/generateMusic";
import type {
  GenerateMusicResult,
  UploadStatus,
} from "../utils/generateMusic";
import type { GenerateCompositionPlanResponse } from "../utils/generateCompositionPlan";

export type StreamPhase =
  | "idle"
  | "connecting"
  | "streaming"
  | "complete"
  | "error";

export const MIN_STREAM_PLAYBACK_BYTES = 16_384;

interface GenerateMusicVariables {
  prompt?: string;
  compositionPlan?: GenerateCompositionPlanResponse;
  musicLen?: number;
  forcedInstrumental: boolean;
  guidedSelections?: Record<string, string[]>;
}

const useGenerateMusic = () => {
  const [data, setData] = useState<GenerateMusicResult | undefined>();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [streamPhase, setStreamPhase] = useState<StreamPhase>("idle");
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const abortRef = useRef<AbortController | null>(null);
  const prevBlobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const nextUrl = data?.audioBlobUrl ?? null;
    const prevUrl = prevBlobUrlRef.current;
    if (prevUrl && prevUrl !== nextUrl) {
      // Defer revoke so Wavesurfer can finish loading the previous URL if needed.
      const urlToRevoke = prevUrl;
      window.setTimeout(() => URL.revokeObjectURL(urlToRevoke), 0);
    }
    prevBlobUrlRef.current = nextUrl;
  }, [data?.audioBlobUrl]);

  const reset = useCallback(() => {
    if (prevBlobUrlRef.current) {
      URL.revokeObjectURL(prevBlobUrlRef.current);
      prevBlobUrlRef.current = null;
    }
    abortRef.current?.abort();
    abortRef.current = null;
    setData(undefined);
    setError(null);
    setIsPending(false);
    setStreamPhase("idle");
    setUploadStatus("idle");
  }, []);

  const updateMusicResult = useCallback((result: GenerateMusicResult) => {
    setData(result);
  }, []);

  const mutate = useCallback((variables: GenerateMusicVariables) => {
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    setIsPending(true);
    setError(null);
    setStreamPhase("connecting");
    setUploadStatus("idle");
    setData(undefined);

    void (async () => {
      try {
        const result = await generateMusic({
          ...variables,
          signal: controller.signal,
          callbacks: {
            onJobSubmitted: () => setStreamPhase("streaming"),
          },
        });

        setStreamPhase("complete");
        setData(result);
        setUploadStatus(result.uploadStatus);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          setStreamPhase("idle");
          return;
        }
        setError(err instanceof Error ? err : new Error(String(err)));
        setStreamPhase("error");
      } finally {
        setIsPending(false);
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    })();
  }, []);

  return {
    data,
    isPending,
    isError: !!error,
    error,
    mutate,
    updateMusicResult,
    reset,
    streamPhase,
    liveAudioUrl: null,
    uploadStatus,
    bytesReceived: 0,
    livePlaybackHandedOff: true,
    completeLivePlayback: () => {},
    isStreamPlaybackReady: true,
  };
};

export default useGenerateMusic;
